import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';

/** Aligné avec firestore.rules (isAdmin) */
export const ADMIN_EMAILS = [
  'xavier@shopiweb.fr',
  'contact@shopiweb.fr',
  'julien@shopiweb.fr',
] as const;

const normalizeEmail = (email?: string | null): string =>
  String(email || '').trim().toLowerCase();

export const isAdminEmail = (email?: string | null): boolean =>
  (ADMIN_EMAILS as readonly string[]).includes(normalizeEmail(email));

export const initializeUser = async (userId: string, email: string) => {
  try {
    const usersRef = collection(db, 'users');
    const userRef = doc(usersRef, userId);
    const normalizedEmail = normalizeEmail(email);
    const shouldBeAdmin = isAdminEmail(normalizedEmail);

    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      await setDoc(userRef, {
        role: shouldBeAdmin ? 'admin' : 'user',
        email: normalizedEmail,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      return shouldBeAdmin;
    }

    const userData = userDoc.data();
    const hasAdminRole = userData.role === 'admin';

    // Corrige les comptes admin connus sans role admin (ex: contact@)
    if (shouldBeAdmin && !hasAdminRole) {
      await setDoc(
        userRef,
        {
          role: 'admin',
          email: normalizedEmail || userData.email || '',
          updatedAt: new Date().toISOString()
        },
        { merge: true }
      );
      return true;
    }

    return hasAdminRole || shouldBeAdmin || isAdminEmail(userData.email);
  } catch (error) {
    console.error('Error initializing user:', error);
    return false;
  }
};

export const checkIsAdmin = async (
  userId: string,
  email?: string | null
): Promise<boolean> => {
  try {
    if (isAdminEmail(email)) {
      return true;
    }

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const adminByRole = userData.role === 'admin';
      const adminByEmail = isAdminEmail(userData.email);

      if (adminByEmail && !adminByRole) {
        await setDoc(
          userRef,
          { role: 'admin', updatedAt: new Date().toISOString() },
          { merge: true }
        );
      }

      return adminByRole || adminByEmail;
    }

    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return isAdminEmail(email);
  }
};
